import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, ClipboardCheck, Plus, Edit, X, Loader2, Camera, ChevronDown, ChevronUp, AlertCircle, ShieldCheck, User } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/ui/toaster';
import api from '../../services/api';

const ClientReviewChecklistManagement = () => {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [formData, setFormData] = useState({
        itemName: '',
        description: '',
        groupName: 'General',
        isRequired: true,
        isPhotoRequired: false,
        requiresSupervisorApproval: false,
        isActive: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [collapsedGroups, setCollapsedGroups] = useState(new Set());

    const pageSize = 50;
    const totalPages = Math.ceil(totalCount / pageSize);

    useEffect(() => {
        fetchItems();
    }, [currentPage]);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const response = await api.getClientReviewChecklistItems(currentPage, pageSize);
            setItems(response.items || []);
            setTotalCount(response.totalCount);
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to fetch client review checklist items',
                variant: 'destructive'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.createClientReviewChecklistItem({
                itemName: formData.itemName,
                description: formData.description,
                groupName: formData.groupName,
                isRequired: formData.isRequired,
                isPhotoRequired: formData.isPhotoRequired,
                requiresSupervisorApproval: formData.requiresSupervisorApproval,
                isActive: formData.isActive,
            });
            toast({ title: 'Success', description: 'Checklist item added successfully' });
            setShowAddModal(false);
            resetForm();
            fetchItems();
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to add item';
            toast({ title: 'Error', description: errorMessage, variant: 'destructive' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.updateClientReviewChecklistItem(selectedItem.id, {
                itemNumber: selectedItem.itemNumber,
                itemName: formData.itemName,
                description: formData.description,
                groupName: formData.groupName,
                groupDisplayOrder: selectedItem.groupDisplayOrder,
                isRequired: formData.isRequired,
                isPhotoRequired: formData.isPhotoRequired,
                requiresSupervisorApproval: formData.requiresSupervisorApproval,
                isActive: formData.isActive,
            });
            toast({ title: 'Success', description: 'Checklist item updated successfully' });
            setShowEditModal(false);
            setSelectedItem(null);
            fetchItems();
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to update item';
            toast({ title: 'Error', description: errorMessage, variant: 'destructive' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this item?')) return;
        try {
            await api.deleteClientReviewChecklistItem(id);
            toast({ title: 'Success', description: 'Checklist item deleted successfully' });
            fetchItems();
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to delete item';
            toast({ title: 'Error', description: errorMessage, variant: 'destructive' });
        }
    };

    const resetForm = () => {
        setFormData({
            itemName: '',
            description: '',
            groupName: 'General',
            isRequired: true,
            isPhotoRequired: false,
            requiresSupervisorApproval: false,
            isActive: true,
        });
    };

    const openAddModal = () => {
        resetForm();
        setSelectedItem(null);
        setShowAddModal(true);
    };

    const openEditModal = (item) => {
        setSelectedItem(item);
        setFormData({
            itemName: item.itemName,
            description: item.description || '',
            groupName: item.groupName || 'General',
            isRequired: item.isRequired,
            isPhotoRequired: item.isPhotoRequired || false,
            requiresSupervisorApproval: item.requiresSupervisorApproval || false,
            isActive: item.isActive,
        });
        setShowEditModal(true);
    };

    const toggleGroupCollapse = (groupName) => {
        setCollapsedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupName)) {
                newSet.delete(groupName);
            } else {
                newSet.add(groupName);
            }
            return newSet;
        });
    };

    const renderForm = (onSubmit, isEdit) => (
        <form onSubmit={onSubmit} className="space-y-4">
            <div>
                <Label htmlFor="itemName">Item Name *</Label>
                <Input
                    id="itemName"
                    value={formData.itemName}
                    onChange={(e) => setFormData({ ...formData, itemName: e.target.value })}
                    required
                    placeholder="e.g. Check wall alignment"
                />
            </div>
            <div>
                <Label htmlFor="groupName">Group Name *</Label>
                <Input
                    id="groupName"
                    value={formData.groupName}
                    onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
                    required
                    placeholder="General"
                />
            </div>
            <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    placeholder="Optional description of the review item"
                />
            </div>
            <div className="space-y-2">
                <div className="flex items-center space-x-2">
                    <input
                        type="checkbox"
                        id="isRequired"
                        checked={formData.isRequired}
                        onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                        className="rounded border-gray-300"
                    />
                    <Label htmlFor="isRequired">Required</Label>
                </div>
                <div className="flex items-center space-x-2">
                    <input
                        type="checkbox"
                        id="isPhotoRequired"
                        checked={formData.isPhotoRequired}
                        onChange={(e) => setFormData({ ...formData, isPhotoRequired: e.target.checked })}
                        className="rounded border-gray-300"
                    />
                    <Label htmlFor="isPhotoRequired">Photo Required</Label>
                </div>
                <div className="flex items-center space-x-2">
                    <input
                        type="checkbox"
                        id="requiresSupervisorApproval"
                        checked={formData.requiresSupervisorApproval}
                        onChange={(e) => setFormData({ ...formData, requiresSupervisorApproval: e.target.checked })}
                        className="rounded border-gray-300"
                    />
                    <Label htmlFor="requiresSupervisorApproval">Supervisor Approval Required</Label>
                </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)} disabled={submitting}>
                    Cancel
                </Button>
                <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700" disabled={submitting}>
                    {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{isEdit ? 'Saving...' : 'Adding...'}</> : (isEdit ? 'Save Changes' : 'Add Item')}
                </Button>
            </div>
        </form>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <Toaster />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-8">
                    <Button variant="ghost" onClick={() => navigate('/admin/settings')} className="mb-4">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to Settings
                    </Button>

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-xl flex items-center justify-center shrink-0">
                                <ClipboardCheck className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Client Review Checklist Master</h1>
                                <p className="text-gray-600 text-sm sm:text-base">Total {totalCount} checklist items</p>
                            </div>
                        </div>
                        <Button className="bg-cyan-600 hover:bg-cyan-700 text-white self-start sm:self-auto" onClick={openAddModal}>
                            <Plus className="mr-2 h-4 w-4" />
                            Add Checklist Item
                        </Button>
                    </div>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>All Client Review Checklist Items</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex justify-center items-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
                            </div>
                        ) : items.length === 0 ? (
                            <div className="text-center py-12 bg-gray-50 rounded-lg">
                                <AlertCircle className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                                <p className="text-gray-600 mb-4">No client review checklist items yet</p>
                                <Button onClick={openAddModal} variant="outline" className="border-cyan-600 text-cyan-600 hover:bg-cyan-50">
                                    <Plus className="h-4 w-4 mr-2" /> Add First Item
                                </Button>
                            </div>
                        ) : (
                            <>
                                <div className="space-y-6">
                                    {(() => {
                                        const groupedItems = items.reduce((acc, item) => {
                                            const groupKey = item.groupName || 'General';
                                            if (!acc[groupKey]) {
                                                acc[groupKey] = {
                                                    groupName: groupKey,
                                                    groupDisplayOrder: item.groupDisplayOrder ?? 999,
                                                    items: []
                                                };
                                            }
                                            acc[groupKey].items.push(item);
                                            return acc;
                                        }, {});

                                        const sortedGroups = Object.values(groupedItems)
                                            .sort((a, b) => a.groupDisplayOrder - b.groupDisplayOrder);

                                        return sortedGroups.map((group, groupIndex) => {
                                            const sortedItems = group.items.sort((a, b) =>
                                                a.itemNumber - b.itemNumber || a.id.localeCompare(b.id)
                                            );
                                            const isCollapsed = collapsedGroups.has(group.groupName);

                                            return (
                                                <div key={groupIndex} className="space-y-3">
                                                    <div
                                                        className="flex items-center gap-3 mb-3 cursor-pointer hover:opacity-75 transition-opacity"
                                                        onClick={() => toggleGroupCollapse(group.groupName)}
                                                    >
                                                        <div className="h-px bg-gray-300 flex-1"></div>
                                                        <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 transition-colors">
                                                            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                                                                {group.groupName}
                                                            </h4>
                                                            <span className="text-xs text-gray-500">({sortedItems.length})</span>
                                                            {isCollapsed ? (
                                                                <ChevronDown className="h-4 w-4 text-gray-600" />
                                                            ) : (
                                                                <ChevronUp className="h-4 w-4 text-gray-600" />
                                                            )}
                                                        </div>
                                                        <div className="h-px bg-gray-300 flex-1"></div>
                                                    </div>

                                                    {!isCollapsed && sortedItems.map((item) => (
                                                        <div
                                                            key={item.id}
                                                            className="p-4 bg-white border border-gray-200 rounded-lg hover:border-cyan-300 transition-all"
                                                        >
                                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                                                                <div className="flex items-start sm:items-center gap-2 flex-1 min-w-0">
                                                                    <span className="w-8 h-8 bg-cyan-100 text-cyan-700 rounded-full flex items-center justify-center text-sm font-semibold shrink-0">
                                                                        {item.itemNumber}
                                                                    </span>
                                                                    <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-2">
                                                                        <h4 className="font-semibold text-gray-900 break-words sm:truncate">{item.itemName}</h4>
                                                                        <div className="flex flex-wrap items-center gap-2">
                                                                            {item.isRequired && (
                                                                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded shrink-0">
                                                                                    Required
                                                                                </span>
                                                                            )}
                                                                            {item.isPhotoRequired && (
                                                                                <span className="text-xs text-blue-600 flex items-center gap-1 shrink-0">
                                                                                    <Camera className="h-3 w-3" /> Photo
                                                                                </span>
                                                                            )}
                                                                            {item.requiresSupervisorApproval && (
                                                                                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded flex items-center gap-1 shrink-0">
                                                                                    <ShieldCheck className="h-3 w-3" /> Supervisor Approval
                                                                                </span>
                                                                            )}
                                                                            {!item.isActive && (
                                                                                <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded shrink-0">
                                                                                    Inactive
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="flex flex-wrap items-center gap-3 shrink-0 self-end sm:self-auto">
                                                                    {item.createdBy && (
                                                                        <span className="text-xs text-gray-500 flex items-center gap-1">
                                                                            <User className="h-3 w-3" /> {item.createdBy}
                                                                        </span>
                                                                    )}
                                                                    <div className="flex gap-2">
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            onClick={() => openEditModal(item)}
                                                                            className="h-8 w-8"
                                                                        >
                                                                            <Edit className="h-4 w-4" />
                                                                        </Button>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            onClick={() => handleDelete(item.id)}
                                                                            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                        >
                                                                            <X className="h-4 w-4" />
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {item.description && (
                                                                <p className="text-sm text-gray-600 ml-10 mt-2">{item.description}</p>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>

                                {totalPages > 1 && (
                                    <div className="flex items-center justify-between border-t pt-4 mt-4">
                                        <div className="text-sm text-gray-500">
                                            Page {currentPage} of {totalPages} ({totalCount} total)
                                        </div>
                                        <div className="flex gap-2">
                                            <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                                                Previous
                                            </Button>
                                            <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                                                Next
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Add Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-4">
                    <Card className="w-full max-w-md mx-auto my-8">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle>Add Client Review Item</CardTitle>
                                <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {renderForm(handleAdd, false)}
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-4">
                    <Card className="w-full max-w-md mx-auto my-8">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle>Edit Client Review Item</CardTitle>
                                <Button variant="ghost" size="sm" onClick={() => setShowEditModal(false)}>
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {renderForm(handleEdit, true)}
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default ClientReviewChecklistManagement;
